import { SessionClockTime, SessionTimeRange } from '@/components/SessionClockTime'
import { resolveAgendaDayLabel } from '@/lib/agendaDayLabel'
import { formatAgendaDayDate } from '@/lib/agendaDayDate'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'

export type AgendaLayout = 'list' | 'cards' | 'timeline' | 'table'

export interface AgendaDayItem {
  id: number
  time: string
  endTime?: string
  description: string
  groupLabel?: string | null
  soldOut?: boolean
  disabled?: boolean
}

export interface AgendaDay {
  id: number
  number: number
  label?: string
  date?: string | null
  dayItems: AgendaDayItem[]
}

interface Theme {
  primaryColor: string
  regTextColor?: string
  regInputBorderColor?: string
  regCardBgColor?: string
}

interface SessionGroup {
  label: string | null
  items: AgendaDayItem[]
}

function groupDayItems(items: AgendaDayItem[], enabled = true): SessionGroup[] {
  if (items.length === 0) return []
  if (!enabled) return [{ label: null, items }]

  const groups: SessionGroup[] = []
  let currentLabel: string | null | undefined
  let currentItems: AgendaDayItem[] = []

  const flush = () => {
    if (currentItems.length === 0) return
    groups.push({ label: currentLabel ?? null, items: currentItems })
    currentItems = []
  }

  for (const item of items) {
    const label = item.groupLabel?.trim() || null
    if (currentLabel !== undefined && label !== currentLabel) flush()
    currentLabel = label
    currentItems.push(item)
  }

  flush()
  return groups
}

function isSessionLocked(item: AgendaDayItem) {
  return !!(item.soldOut || item.disabled)
}

function SessionGroupSection({
  label,
  isFirst,
  children,
}: {
  label: string | null
  isFirst: boolean
  children: React.ReactNode
}) {
  return (
    <div className={`ev-reg-session-group ${isFirst ? '' : 'ev-reg-session-group-separated'}`}>
      {label && (
        <div className="ev-reg-session-group-heading mb-2 text-xs font-semibold uppercase tracking-wide opacity-80">
          {label}
        </div>
      )}
      {children}
    </div>
  )
}

interface Props {
  days: AgendaDay[]
  selected: Set<number>
  onToggle: (id: number) => void
  layout: AgendaLayout
  dayLabel: string
  fromLabel: string
  toLabel: string
  locale: string
  theme: Theme
  groupSessions?: boolean
  showFromToLabels?: boolean
  allSeatsBookedLabel?: string
}

function SessionTime({ item, locale }: { item: AgendaDayItem; locale: string }) {
  if (!item.time && !item.endTime) return null

  return (
    <SessionTimeRange
      start={item.time}
      end={item.endTime}
      locale={locale}
      className="ev-reg-session-time text-xs tabular-nums"
    />
  )
}

function SessionDescription({ html }: { html: string }) {
  if (!html) return null

  return (
    <div
      className="ev-reg-session-description ev-reg-session-description-html text-sm font-medium [&_p]:m-0 [&_p+p]:mt-1 [&_ul]:my-1 [&_ul]:ps-5 [&_ul]:list-disc [&_strong]:font-semibold"
      dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(html) }}
    />
  )
}

function SoldOutBadge({ label }: { label: string }) {
  return (
    <span className="ev-reg-session-sold-out mt-1 inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-red-600">
      {label}
    </span>
  )
}

function SessionContent({
  item,
  locale,
  allSeatsBookedLabel,
}: {
  item: AgendaDayItem
  locale: string
  allSeatsBookedLabel: string
}) {
  return (
    <div>
      <SessionTime item={item} locale={locale} />
      <SessionDescription html={item.description} />
      {isSessionLocked(item) && <SoldOutBadge label={allSeatsBookedLabel} />}
    </div>
  )
}

function DayHeading({ day, dayLabel, locale }: { day: AgendaDay; dayLabel: string; locale: string }) {
  const title = resolveAgendaDayLabel(day.number, dayLabel, locale, day.label)
  const dateLabel = formatAgendaDayDate(day.date, locale)

  return (
    <div className="ev-reg-session-day-heading">
      <div className="ev-reg-session-day-title text-base font-bold tracking-wide">{title}</div>
      {dateLabel && (
        <div className="ev-reg-session-day-date text-xs font-normal normal-case tracking-normal opacity-70">
          {dateLabel}
        </div>
      )}
    </div>
  )
}

function ListLayout({
  days,
  selected,
  onToggle,
  dayLabel,
  locale,
  theme,
  groupSessions = true,
  allSeatsBookedLabel = 'All seats booked',
}: Omit<Props, 'layout'>) {
  return (
    <>
      {days.map((day) => (
        <div key={day.id} className="ev-reg-session-day">
          <h2 className="mb-3">
            <DayHeading day={day} dayLabel={dayLabel} locale={locale} />
          </h2>
          <div className="ev-reg-session-list space-y-4">
            {groupDayItems(day.dayItems, groupSessions).map((group, groupIndex) => (
              <SessionGroupSection key={`${day.id}-group-${groupIndex}`} label={group.label} isFirst={groupIndex === 0}>
                <div className="space-y-2">
                  {group.items.map((item) => {
                    const locked = isSessionLocked(item)
                    return (
                      <label
                        key={item.id}
                        className={`ev-reg-session-item ev-reg-session-list-item flex items-start gap-3 rounded-md border px-4 py-3 ${
                          locked
                            ? 'ev-reg-session-sold-out-item cursor-not-allowed opacity-60'
                            : `cursor-pointer hover:opacity-90 ${selected.has(item.id) ? 'ev-reg-session-selected' : ''}`
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          disabled={locked}
                          onChange={() => {
                            if (!locked) onToggle(item.id)
                          }}
                          className="mt-1 h-4 w-4 rounded"
                          style={{ accentColor: theme.primaryColor }}
                        />
                        <SessionContent item={item} locale={locale} allSeatsBookedLabel={allSeatsBookedLabel} />
                      </label>
                    )
                  })}
                </div>
              </SessionGroupSection>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function CardsLayout({
  days,
  selected,
  onToggle,
  dayLabel,
  locale,
  theme,
  groupSessions = true,
  allSeatsBookedLabel = 'All seats booked',
}: Omit<Props, 'layout'>) {
  return (
    <>
      {days.map((day) => (
        <div key={day.id}>
          <h2 className="mb-3">
            <DayHeading day={day} dayLabel={dayLabel} locale={locale} />
          </h2>
          <div className="space-y-4">
            {groupDayItems(day.dayItems, groupSessions).map((group, groupIndex) => (
              <SessionGroupSection key={`${day.id}-group-${groupIndex}`} label={group.label} isFirst={groupIndex === 0}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {group.items.map((item) => {
                    const isSelected = selected.has(item.id)
                    const locked = isSessionLocked(item)
                    return (
                      <label
                        key={item.id}
                        className={`ev-reg-session-item ev-reg-session-card flex h-full flex-col rounded-lg border p-4 transition-shadow ${
                          locked
                            ? 'ev-reg-session-sold-out-item cursor-not-allowed opacity-60'
                            : `cursor-pointer hover:shadow-sm ${isSelected ? 'ev-reg-session-selected' : ''}`
                        }`}
                      >
                        <div className="mb-3 flex items-start justify-between gap-2">
                          {(item.time || item.endTime) ? (
                            <span className="ev-reg-session-time-badge rounded-full px-2 py-0.5 text-xs tabular-nums">
                              <SessionTimeRange start={item.time} end={item.endTime} locale={locale} />
                            </span>
                          ) : (
                            <span />
                          )}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={locked}
                            onChange={() => {
                              if (!locked) onToggle(item.id)
                            }}
                            className="h-4 w-4 rounded"
                            style={{ accentColor: theme.primaryColor }}
                          />
                        </div>
                        <SessionDescription html={item.description} />
                        {locked && <SoldOutBadge label={allSeatsBookedLabel} />}
                      </label>
                    )
                  })}
                </div>
              </SessionGroupSection>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function TimelineLayout({
  days,
  selected,
  onToggle,
  dayLabel,
  locale,
  theme,
  groupSessions = true,
  allSeatsBookedLabel = 'All seats booked',
}: Omit<Props, 'layout'>) {
  return (
    <>
      {days.map((day) => (
        <div key={day.id}>
          <h2 className="mb-4">
            <DayHeading day={day} dayLabel={dayLabel} locale={locale} />
          </h2>
          <div className="space-y-4">
            {groupDayItems(day.dayItems, groupSessions).map((group, groupIndex) => (
              <SessionGroupSection key={`${day.id}-group-${groupIndex}`} label={group.label} isFirst={groupIndex === 0}>
                <div className="relative space-y-0">
                  {group.items.map((item, index) => {
                    const isSelected = selected.has(item.id)
                    const isLast = index === group.items.length - 1
                    const locked = isSessionLocked(item)
                    return (
                      <div key={item.id} className="relative flex gap-4 pb-6">
                        {!isLast && (
                          <div
                            className="ev-reg-session-timeline-line absolute left-[7.5rem] top-8 bottom-0 w-px sm:left-[8.5rem]"
                            aria-hidden
                          />
                        )}
                        <div className="ev-reg-session-time ev-reg-session-timeline-time w-28 shrink-0 pt-1 text-right text-xs sm:w-32">
                          {(item.time || item.endTime) ? (
                            <SessionTimeRange start={item.time} end={item.endTime} locale={locale} />
                          ) : (
                            '—'
                          )}
                        </div>
                        <div className="relative shrink-0 pt-1.5">
                          <span
                            className={`ev-reg-session-timeline-dot block h-3 w-3 rounded-full border-2 ${isSelected ? 'ev-reg-session-timeline-dot-selected' : ''}`}
                          />
                        </div>
                        <div
                          className={`ev-reg-session-item ev-reg-session-timeline-item min-w-0 flex-1 rounded-md border px-4 py-3 ${
                            locked
                              ? 'ev-reg-session-sold-out-item cursor-not-allowed opacity-60'
                              : `cursor-pointer hover:opacity-90 ${isSelected ? 'ev-reg-session-selected' : ''}`
                          }`}
                          onClick={() => {
                            if (!locked) onToggle(item.id)
                          }}
                          onKeyDown={(e) => {
                            if (locked) return
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onToggle(item.id)
                            }
                          }}
                          role="button"
                          tabIndex={locked ? -1 : 0}
                          aria-disabled={locked}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={locked}
                              onChange={() => {
                                if (!locked) onToggle(item.id)
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 h-4 w-4 rounded"
                              style={{ accentColor: theme.primaryColor }}
                            />
                            <div>
                              <SessionDescription html={item.description} />
                              {locked && <SoldOutBadge label={allSeatsBookedLabel} />}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </SessionGroupSection>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function TableLayout({
  days,
  selected,
  onToggle,
  dayLabel,
  fromLabel,
  toLabel,
  locale,
  theme,
  groupSessions = true,
  showFromToLabels = true,
  allSeatsBookedLabel = 'All seats booked',
}: Omit<Props, 'layout'>) {
  const isRtl = locale.startsWith('ar')

  return (
    <>
      {days.map((day) => (
        <div
          key={day.id}
          className="ev-reg-session-day ev-reg-session-table-day overflow-hidden rounded-lg border"
        >
          <div
            className="ev-reg-session-table-header grid grid-cols-[auto_minmax(0,1fr)_5.5rem_5.5rem] items-center gap-x-3 border-b px-4 py-2.5 text-xs"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <span aria-hidden="true" />
            <DayHeading day={day} dayLabel={dayLabel} locale={locale} />
            <span className="ev-reg-session-table-from-label text-center opacity-70">
              {showFromToLabels ? fromLabel : ''}
            </span>
            <span className="ev-reg-session-table-to-label border-s ps-3 text-center opacity-70">
              {showFromToLabels ? toLabel : ''}
            </span>
          </div>

          {groupDayItems(day.dayItems, groupSessions).map((group, groupIndex) => (
            <div key={`${day.id}-group-${groupIndex}`} className={groupIndex > 0 ? 'ev-reg-session-group-separated border-t' : ''}>
              {group.label && (
                <div className="ev-reg-session-group-heading border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide opacity-80">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => {
                const isSelected = selected.has(item.id)
                const locked = isSessionLocked(item)

                return (
                  <label
                    key={item.id}
                    className={`ev-reg-session-item ev-reg-session-table-row grid grid-cols-[auto_minmax(0,1fr)_5.5rem_5.5rem] items-start gap-x-3 border-b px-4 py-3 last:border-b-0 ${
                      locked
                        ? 'ev-reg-session-sold-out-item cursor-not-allowed opacity-60'
                        : `cursor-pointer hover:opacity-90 ${isSelected ? 'ev-reg-session-selected' : ''}`
                    }`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={locked}
                      onChange={() => {
                        if (!locked) onToggle(item.id)
                      }}
                      className="mt-1 h-4 w-4 shrink-0 rounded"
                      style={{ accentColor: theme.primaryColor }}
                    />
                    <div>
                      <SessionDescription html={item.description} />
                      {locked && <SoldOutBadge label={allSeatsBookedLabel} />}
                    </div>
                    <div className="ev-reg-session-time ev-reg-session-table-from pt-0.5 text-center text-sm">
                      <SessionClockTime value={item.time} locale={locale} />
                    </div>
                    <div className="ev-reg-session-time ev-reg-session-table-to border-s ps-3 pt-0.5 text-center text-sm">
                      <SessionClockTime value={item.endTime} locale={locale} />
                    </div>
                  </label>
                )
              })}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

export default function RegistrationAgendaSessions(props: Props) {
  const { layout, ...rest } = props

  switch (layout) {
    case 'cards':
      return <CardsLayout {...rest} />
    case 'timeline':
      return <TimelineLayout {...rest} />
    case 'table':
      return <TableLayout {...rest} />
    default:
      return <ListLayout {...rest} />
  }
}
